import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import * as moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { roleSettings } from "../../services/role/role-settings";
import { HttpService } from '../../services/http/http.service';
import { LoaderService } from '../../services/loader/loader.service';
import { MovePatientPopupComponent } from '../../dashboard/move-patient-popup/move-patient-popup.component';
import { Location } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { InvoiceTemplatesPopupComponent } from '../../shared/component/invoice-templates-popup/invoice-templates-popup.component';
import { CommunicationService } from '../../services/communication/communication.service';

/**
 * This component shows the detail of the patient including profile, survey, documents, and engagement tabs. Each tab contains a subcomponent. It also contains an activity component which shows the activities for particular patient 
 * 
 * This is a reusable component, we have used this component as popup and page.
 * 
 * a.	Print and Export 
 * 
 * On click of Print and Export option it shows the list of available templates in popup. CareAlly can select one of the template and print it.
 * 
 * b.	Add to Queue
 * 
 * CareAlly can able to add the patient to the particular dashboard queue, If that patient already part of any queue then system throws an error.
 */
@Component({
  selector: 'app-patient-detail',
  templateUrl: './patient-detail.component.html',
  styleUrls: ['./patient-detail.component.scss']
})
export class PatientDetailComponent implements OnInit {
  userPageSettings = roleSettings.superAdmin.patientDetail;
  @Input() popupData:any = {};
  @Output() public closePopup = new EventEmitter();
  isDocumentTabActivated = false;
  patientDetails:any = {
    id: '',
    fname: '',
    lname: '',
    birthDate: '',
    phone: '',
    email: '',
    addrLine1: '',
    addrLine2: '',
    text: '',
    city: '',
    state: '',
    postalCode: '',
    mrn: '',
    emailStats: null,
    mobileStats: null 
  };

  summaryScreenConfig = {
    title: 'Patient Summary',
    primaryBtnText: 'Update',
    secondaryBtnText: 'Cancel',
    editOption: true
  }

  patientId = null;
  tabs = {'profile': true, 'assessment': true, 'appointment': true,'documents': true, 'patientEngagement': false, 'workflow': false, 'carePlan': false, 'socialDeterminants': false, 'rewards': false}
  appointmentCount = 0;
  assessmentCount = 0;
  documentsCount = 0;
  printTemplates = [];
  isSkeleton = true;
  actionTemplates = [];
  serviceListeners = [];

  constructor(private httpService: HttpService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private location: Location,
    private dialog: MatDialog,
    private communicationService: CommunicationService,
    private activeRoute: ActivatedRoute) {
      let sub;
      sub = this.activeRoute.params.subscribe(params => {
        this.patientId = params.id
      });
      this.serviceListeners.push(sub);

      sub = this.communicationService.getTemplates.subscribe(templates =>{
        this.printTemplates = templates.filter(x => x.templateType === 'printTemplate');
        this.actionTemplates = templates.filter(x => x.templateType === 'actionTemplate');
      })
      this.serviceListeners.push(sub);
  }

  ngOnInit() {
    if(this.popupData.patientId){
      this.patientId = this.popupData.patientId
    }

    if(this.patientId)
      this.getPatient();
  }

  ngOnDestroy(): void {
    if(this.serviceListeners.length){
      this.serviceListeners.map(item=>{
        item.unsubscribe();
      })
    }
  }

  getAnnualWellnessVisitDate(){
    return moment().add(30, 'days').format('l')
  }

  onEditProfile(event){
    if(event)
      this.getPatient();
  }

  /**
   * This function is used to get the patient detail based on patient id
   */
  getPatient() {
    this.communicationService.resetTemplateData();
    this.isSkeleton = true;
    this.loader.show('progress');
    this.httpService.get(`patient/${this.patientId}`)
      .subscribe((response: any) => {
        this.patientDetails = this.formatUserData(response);
        this.isSkeleton = false;
        this.communicationService.setTemplateData(this.patientDetails, 'patient');
        this.communicationService.setTemplateData(null, 'documents'); 
        this.communicationService.setTemplateData(null, 'allDocuments'); 
        this.loader.hide('progress');
      }, error => {
        this.isSkeleton = false;
        this.loader.hide('progress');
        this.toastr.warning(error, 'Patients', {
          progressBar: true, timeOut: 3000
        });
      })
  }

/**
 * After receiving the data from the backend, we format that data according to frontend need
 * @param data 
 */
  formatUserData(data){
    let lname = '';
    let fname = '';
    if(data.name&&data.name.length&&data.name[0].given&&data.name[0].given.length){
      fname = data.name[0].given.join(' ');
      lname = data.name[0].family;
    }else{
      const fullname = data.name&&data.name[0].text.split(' ') || [];
      if(fullname.length>1){
        lname = fullname.splice(fullname.length-1, 1)[0];
        fname = fullname.join(' ');
      }else{
        fname = fullname.length? fullname[0] : ''
      }
    }
    let mrn = '';
    if(data.identifier && data.identifier.length){
      const temp = data.identifier.filter(x=> x.system.includes('mrn'));
      if(temp.length)
        mrn = temp[0].value
    }
    const telecom = {};
    data.telecom&&data.telecom.length&&data.telecom.map(x=>{
      telecom[x.system] = x.value;
    })
    
    // Address
    let addrLine1 = '';
    let addrLine2 = '';
    if(data.address&&data.address.length&&data.address[0].line&&data.address[0].line.length){
      addrLine1 = data.address[0].line[0];
      if(data.address[0].line.length>1)
        addrLine2 = data.address[0].line[1];
    }else if(data.address&&data.address.length){
      addrLine1 = data.address[0].text;
    }

    const result = {
      id: data.id,
      fname: fname,
      lname: lname,
      birthDate: data.birthDate? moment(data.birthDate).utc().format() : '',
      ...telecom,
      addrLine1,
      addrLine2,
      text: data.address&&data.address[0].text || '',
      city: data.address&&data.address[0].city || '',
      state: data.address&&data.address[0].state || '',
      postalCode: data.address&&data.address[0].postalCode || '',
      mrn,
      emailStats: data.emailStats || null,
      mobileStats: data.mobileStats || null
    };
    return result;
  }

  /**
   * This function get trigger when the user changes the tab
   * @param index 
   */
  onTabChange(index){
    this.tabs[Object.keys(this.tabs)[index]] = true;
    this.isDocumentTabActivated = Object.keys(this.tabs)[index] === 'documents';
  }

/**
 * This function is used to going back from where the user has come
 */
  onBack(){
    this.location.back();
  }

  /**
   * Suppose the user wants to move the patient to a particular queue then this function gets called.
   */
  selectQueue(){
    const dialogRef = this.dialog.open(MovePatientPopupComponent, {
      width: '450px',
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Move Patient',
        subTitle: 'Select the queue to move the patient',
        queueId: null,
        patient: {name: `${this.patientDetails.fname} ${this.patientDetails.lname}`},
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if(res&&res.data){
        delete res.data.moveLabel;
        this.movePatient(res);
      }
    });
  }

  /**
   * Once the user selects the destination board and queue then this function gets called. 
   * 
   * This function is used to call the move patient API
   * @param payload 
   */
  movePatient(payload){
    this.loader.show('progress');
    payload = {
      ...payload.data,
      patientId: this.patientId
    }
    this.httpService.post('task/new', payload)
    .subscribe((response: any) => {
      this.loader.hide('progress');
      if(response.message){
        this.toastr.success(response.message, 'Patient', {
          progressBar: true, timeOut: 3000
        });
      }
    }, error =>{
      this.loader.hide('progress');
      this.toastr.warning(error, '', {
        progressBar: true, timeOut: 3000
      });
    })
  }

  /**
   * This function is used to get the count of appointment booked
   * @param event 
   */
  appointmentCountFun(event){
    this.appointmentCount = event;
  }

  /**
   * This function is used to get the count of assessments
   * @param event 
   */
  assessmentCountFun(event){
    this.assessmentCount = event;
  }

  /**
   * This function is used to get the count of documents
   * @param event 
   */
  documentsCountFun(event){
    this.documentsCount = event;
  }

  onClosePopup(){
    this.closePopup.emit(true)
  }

  /**
   * This function gets called when the user selects the print option form UI
   */
  printInvoiceTemplates(){
    const dialogRef = this.dialog.open(InvoiceTemplatesPopupComponent, {
      width: '40vw',
      maxHeight: '95vh',
      panelClass: 'custom-dialog-container',
      disableClose: true,
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
    });
  }

}

